<template>
  <div class="cost-center-attribution-entities-list">
    <div class="entities-header mb-4">
      <h3>{{ $t('cost-center.entities') }}</h3>
    </div>

    <!-- Tabs -->
    <v-tabs v-model="tab" class="mb-4">
      <v-tab>
        <v-icon left small>mdi-account</v-icon>
        {{ $t('cost-center.members') }}
        <v-chip class="ml-2" x-small>{{ totalMembers }}</v-chip>
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-ev-station</v-icon>
        {{ $t('cost-center.charging-points') }}
        <v-chip class="ml-2" x-small>{{ totalChargingPoints }}</v-chip>
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-gauge</v-icon>
        {{ $t('cost-center.meter-devices') }}
        <v-chip class="ml-2" x-small>{{ totalMeterDevices }}</v-chip>
      </v-tab>
    </v-tabs>

    <!-- Filters -->
    <div class="filters mb-4">
      <v-chip-group color="primary" v-model="activeFilter" mandatory column>
        <v-chip filter outlined small :value="null" class="my-2">
          {{ $t('cost-center.filter-all') }}
        </v-chip>
        <v-chip filter outlined small value="including-selected" :disabled="!selectedCostCenter" class="my-2">
          {{ $t('cost-center.filter-including-selected') }}
        </v-chip>
        <v-chip filter outlined small value="no-cost-center" class="my-2">
          {{ $t('cost-center.filter-no-cost-center') }}
        </v-chip>
        <v-chip filter outlined small value="single-cost-center" class="my-2">
          {{ $t('cost-center.filter-single-cost-center') }}
        </v-chip>
        <v-chip filter outlined small value="multiple-cost-centers" class="my-2">
          {{ $t('cost-center.filter-multiple-cost-centers') }}
        </v-chip>
        <v-badge
          :content="invalidEntitiesCount"
          :value="invalidEntitiesCount > 0"
          color="error"
          offset-x="20"
          offset-y="20"
        >
          <v-chip filter outlined small value="total-not-100"  class="my-2" :disabled="invalidEntitiesCount === 0">
            {{ $t('cost-center.filter-anomalies') }}
          </v-chip>
        </v-badge>
      </v-chip-group>
    </div>

    <!-- Search -->
    <v-text-field
      v-model="search"
      :label="$t('search')"
      prepend-inner-icon="mdi-magnify"
      clearable
      hide-details
      outlined
      dense
      class="mb-4"
    />

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredEntities"
      :loading="loading"
      :show-select="!!assignMode"
      :items-per-page="-1"
      hide-default-footer
      item-key="uniqueId"
      :item-class="getRowClass"
      class="entities-table clickable-rows"
      @input="onSelectionChange"
      @click:row="handleRowClick"
    >
      <!-- Name column -->
      <template v-slot:item.name="{ item }">
        <div class="entity-name">
          <v-icon small class="mr-2">{{ getEntityIcon(item) }}</v-icon>
          {{ getEntityName(item) }}
        </div>
      </template>

      <!-- Current Cost Centers column -->
      <template v-slot:item.costCenters="{ item }">
        <div v-if="item.costCenters && item.costCenters.length > 0" class="cost-centers-chips">
          <v-chip
            v-for="cc in item.costCenters"
            :key="cc.costCenterId || cc.id"
            x-small
            outlined
            class="mr-1 mb-1"
          >
            {{ cc.costCenterName || cc.name }}
            <span class="ml-1">({{ (cc.sharePercent || 0).toFixed(1) }}%)</span>
          </v-chip>
        </div>
        <span v-else class="grey--text">{{ $t('cost-center.not-assigned') }}</span>
      </template>

      <!-- Status column -->
      <template v-slot:item.status="{ item }">
        <!-- Show nothing if entity has no cost centers -->
        <span v-if="!item.costCenters || item.costCenters.length === 0" class="grey--text">-</span>

        <!-- Show warning icon with tooltip if invalid -->
        <v-tooltip v-else-if="hasAnomaly(item)" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="warning"
              v-bind="attrs"
              v-on="on"
            >
              mdi-alert-circle
            </v-icon>
          </template>
          <span>{{ getAnomalyMessage(item) }}</span>
        </v-tooltip>

        <!-- Show check icon if valid -->
        <v-icon v-else color="success">
          mdi-check-circle
        </v-icon>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import MemberRepository from '@repository/MemberRepository'
import ChargingPointRepository from '@repository/ChargingPointRepository'
import MeterDeviceRepository from '@repository/MeterDeviceRepository'
import OrganizationRepository from '@repository/OrganizationRepository'

export default {
  props: {
    organizationId: {
      type: String,
      required: true
    },
    selectedCostCenter: {
      type: Object,
      default: null
    },
    selectedEntities: {
      type: Array,
      default: () => []
    },
    assignMode: {
      type: String,
      default: null // null, 'assign', or 'unassign'
    },
    selectedEntityId: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      tab: 0,
      search: '',
      activeFilter: null,
      selected: [],
      entities: [],
      loading: false,
      total: 0,
      totalMembers: 0,
      totalChargingPoints: 0,
      totalMeterDevices: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      }
    }
  },

  computed: {
    entityType() {
      const types = ['member', 'charging-point', 'meter-device']
      return types[this.tab]
    },

    headers() {
      return [
        { text: this.$t('name'), value: 'name', align: 'left' },
        { text: this.$t('cost-center.current-allocations'), value: 'costCenters', sortable: false, align: 'center' },
        { text: this.$t('status'), value: 'status', align: 'center', width: '100px' }
      ]
    },

    invalidEntitiesCount() {
      // Count entities that have at least one cost center and are invalid
      return this.entities.filter(e => {
        if (!e.costCenters || e.costCenters.length === 0) return false
        const total = this.getTotalPercent(e)
        const hasZeroPercent = e.costCenters.some(cc => (cc.sharePercent || 0) === 0)
        return Math.abs(total - 100) > 0.001 || hasZeroPercent
      }).length
    },

    filteredEntities() {
      let filtered = this.entities

      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        filtered = filtered.filter(e => {
          const name = this.getEntityName(e).toLowerCase()
          return name.includes(searchLower)
        })
      }

      // Apply predefined filters
      if (this.activeFilter === 'no-cost-center') {
        filtered = filtered.filter(e => !e.costCenters || e.costCenters.length === 0)
      } else if (this.activeFilter === 'single-cost-center') {
        filtered = filtered.filter(e => e.costCenters && e.costCenters.length >= 1)
      } else if (this.activeFilter === 'multiple-cost-centers') {
        filtered = filtered.filter(e => e.costCenters && e.costCenters.length >= 2)
      } else if (this.activeFilter === 'including-selected' && this.selectedCostCenter) {
        filtered = filtered.filter(e =>
          e.costCenters && e.costCenters.some(cc =>
            (cc.costCenterId || cc.id) === this.selectedCostCenter.id
          )
        )
      } else if (this.activeFilter === 'total-not-100') {
        filtered = filtered.filter(e => {
          // Ignore entities with no cost centers at all
          if (!e.costCenters || e.costCenters.length === 0) return false

          const total = this.getTotalPercent(e)
          const hasZeroPercent = e.costCenters.some(cc => (cc.sharePercent || 0) === 0)

          // Include if total != 100% OR at least one cost center is at 0%
          return Math.abs(total - 100) > 0.001 || hasZeroPercent
        })
      }

      // Apply assign/unassign mode filtering
      if (this.assignMode && this.selectedCostCenter) {
        if (this.assignMode === 'assign') {
          // Show only entities that are NOT already assigned to the selected cost center
          filtered = filtered.filter(e => {
            const hasSelectedCC = e.costCenters && e.costCenters.some(cc =>
              (cc.costCenterId || cc.id) === this.selectedCostCenter.id
            )
            return !hasSelectedCC
          })
        } else if (this.assignMode === 'unassign') {
          // Show only entities that ARE already assigned to the selected cost center
          filtered = filtered.filter(e => {
            const hasSelectedCC = e.costCenters && e.costCenters.some(cc =>
              (cc.costCenterId || cc.id) === this.selectedCostCenter.id
            )
            return hasSelectedCC
          })
        }
      }

      return filtered
    }
  },

  watch: {
    tab() {
      this.loadEntities()
      // Close the editor when changing tab to avoid showing wrong entity type
      this.$emit('close-editor')
    },

    assignMode(newMode) {
      // Clear selection when mode changes (no need to reload, filtering is done frontend)
      this.selected = []

      // Reset filter to "All" when entering assign/unassign mode
      if (newMode) {
        this.activeFilter = null
      }
    }
  },

  mounted() {
    this.loadEntitiesCounts()
    this.loadEntities()
  },

  methods: {
    activateTotalNot100Filter() {
      this.activeFilter = 'total-not-100'
    },

    countEntitiesWithInvalidTotal() {
      // Count entities that have at least one cost center and (total != 100% OR has 0% allocation)
      return this.entities.filter(e => {
        if (!e.costCenters || e.costCenters.length === 0) return false
        const total = this.getTotalPercent(e)
        const hasZeroPercent = e.costCenters.some(cc => (cc.sharePercent || 0) === 0)
        return Math.abs(total - 100) > 0.001 || hasZeroPercent
      }).length
    },

    loadEntitiesCounts() {
      OrganizationRepository.countEntities(this.organizationId)
        .then(result => {
          this.totalMembers = result.members || 0
          this.totalChargingPoints = result.chargingPoints || 0
          this.totalMeterDevices = result.meterDevices || 0
        })
        .catch(err => {
          this.notifyError(err)
        })
    },

    loadEntities() {
      return new Promise((resolve, reject) => {
        this.loading = true

        // Load all entities (no pagination from backend)
        const baseParams = {
          organizationId: this.organizationId,
          offset: 0,
          limit: -1
        }

        let promise

        if (this.entityType === 'member') {
          promise = MemberRepository.search({
            ...baseParams,
            searchText: ''
          })
        } else if (this.entityType === 'charging-point') {
          promise = ChargingPointRepository.search({
            ...baseParams,
            query: ''
          })
        } else {
          promise = MeterDeviceRepository.search({
            ...baseParams,
            searchText: ''
          })
        }

        promise.then(result => {

          // Add unique ID and type to each entity
          this.entities = (result.items || []).map(item => ({
            ...item,
            type: this.entityType,
            uniqueId: `${this.entityType}-${item.id}`,
            costCenters: item.costCenters || []
          }))
          this.total = result.total || 0

          this.loading = false
          resolve(this.entities)
        }).catch(err => {
          this.$dialog.notify.error(err.message || 'Erreur lors du chargement des entités')
          this.loading = false
          reject(err)
        })
      })
    },

    getEntityById(entityId, entityType) {
      return this.entities.find(e => e.id === entityId && e.type === entityType)
    },

    onSelectionChange(selection) {
      this.$emit('select', selection)
    },

    handleRowClick(item) {
      // In assign/unassign mode: toggle checkbox selection
      if (this.assignMode) {
        const index = this.selected.findIndex(s => s.uniqueId === item.uniqueId)
        if (index > -1) {
          // Already selected, remove it
          this.selected.splice(index, 1)
        } else {
          // Not selected, add it
          this.selected.push(item)
        }
        // Emit the change
        this.$emit('select', this.selected)
      } else {
        // Normal mode: open editor
        this.openEntityEditor(item)
      }
    },

    openEntityEditor(item) {
      // Open editor for this entity
      this.$emit('open-editor', item)
    },

    getRowClass(item) {
      // Highlight the row if this entity is currently selected in the editor
      return item.id === this.selectedEntityId ? 'selected-entity-row' : ''
    },

    getEntityIcon(item) {
      const icons = {
        'member': 'mdi-account',
        'charging-point': 'mdi-ev-station',
        'meter-device': 'mdi-gauge'
      }
      return icons[item.type] || 'mdi-help-circle'
    },

    getEntityName(item) {
      if (item.type === 'member') {
        const fullName = `${item.firstname || ''} ${item.lastname || ''}`.trim()
        return fullName || item.customer?.evcoId || '-'
      }
      return item.name || item.identifier || '-'
    },

    getTotalPercent(item) {
      if (!item.costCenters || item.costCenters.length === 0) return 0

      return item.costCenters.reduce((sum, cc) => {
        return sum + (cc.sharePercent || 0)
      }, 0)
    },

    hasAnomaly(item) {
      // An entity has an anomaly if:
      // 1. It has no cost centers at all -> no anomaly (just empty)
      // 2. Total != 100%
      // 3. At least one cost center is at 0%
      if (!item.costCenters || item.costCenters.length === 0) return false

      const total = this.getTotalPercent(item)
      const hasZeroPercent = item.costCenters.some(cc => (cc.sharePercent || 0) === 0)

      return Math.abs(total - 100) > 0.001 || hasZeroPercent
    },

    getAnomalyMessage(item) {
      if (!item.costCenters || item.costCenters.length === 0) return ''

      const total = this.getTotalPercent(item)
      const hasZeroPercent = item.costCenters.some(cc => (cc.sharePercent || 0) === 0)
      const totalNotOk = Math.abs(total - 100) > 0.001

      if (totalNotOk && hasZeroPercent) {
        return this.$t('cost-center.anomaly-total-and-zero')
      } else if (totalNotOk) {
        return this.$t('cost-center.anomaly-total', { total: total.toFixed(1) })
      } else if (hasZeroPercent) {
        return this.$t('cost-center.anomaly-zero')
      }
      return ''
    }
  }
}
</script>

<style lang="scss">
.cost-center-attribution-entities-list {
  .entities-header {
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }
  }


  .action-bar {
    border-radius: 4px;
  }

  .entity-name {
    display: flex;
    align-items: center;
  }

  .cost-centers-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .entities-table {
    th:first-child,
    td:first-child {
      text-align: left !important;
    }

    th:nth-child(2),
    td:nth-child(2) {
      text-align: center !important;
    }

    .v-data-table__wrapper {
      max-height: calc(100vh - 530px);
      overflow-y: auto;
    }

    &.clickable-rows tbody tr {
      cursor: pointer;
    }

    tbody {
      tr {
        // Désactiver l'effet hover par défaut de Vuetify
        &:hover:not(.selected-entity-row) td {
          background-color: transparent !important;
        }

        &.selected-entity-row {
          &.selected-entity-row {
            & > td {
              border-top: 1px solid #e0e0e0;
              border-bottom: 1px solid #e0e0e0 !important;
              background-color: #f5f5f5 !important;
              &:first-child {
                border-left: 4px solid #E53C22 !important;
                padding-left: 12px !important;
                border-top-left-radius: 4px;
                border-bottom-left-radius: 4px;
              }
              &:last-child {
                border-right: 1px solid #e0e0e0;
                border-top-right-radius: 4px;
                border-bottom-right-radius: 4px;
              }
            }
            &:hover > td {
              background-color: #f5f5f5 !important;
            }
          }

        }
      }
    }

  }
}
</style>
